/**
 * Affected-roads list hook
 *
 * Encapsulates add/remove against `form.values.affectedRoads`. Mirrors the
 * shape of `useRequirements` (inline list bound to a Mantine form field), but
 * tailored to the obstruction affected-road input.
 */
import { useCallback } from 'react';
import type { UseFormReturnType } from '@mantine/form';

import { generateId } from '@/utils/helpers';

import type {
  ObstructionAffectedRoadInput,
  ObstructionWizardFormState,
} from '../../../types';

interface UseAffectedRoadsOptions {
  form: UseFormReturnType<ObstructionWizardFormState>;
  isLoading?: boolean;
}

interface UseAffectedRoadsReturn {
  affectedRoads: ObstructionAffectedRoadInput[];
  totalCount: number;
  addAffectedRoad: (location: string, description: string) => void;
  removeAffectedRoad: (index: number) => void;
}

export function useAffectedRoads({
  form,
  isLoading = false,
}: UseAffectedRoadsOptions): UseAffectedRoadsReturn {
  const affectedRoads = form.values.affectedRoads;

  const addAffectedRoad = useCallback(
    (location: string, description: string) => {
      const trimmedLocation = location.trim();
      if (!trimmedLocation || isLoading) return;

      // New rows share one id for both fields (id === tempId), matching how
      // edit-mode hydration seeds existing rows (tempId = server id).
      const id = generateId();
      const newItem: ObstructionAffectedRoadInput = {
        id,
        tempId: id,
        location: trimmedLocation,
        description: description.trim(),
      };

      form.setFieldValue('affectedRoads', [
        ...form.values.affectedRoads,
        newItem,
      ]);
    },
    [form, isLoading]
  );

  const removeAffectedRoad = useCallback(
    (index: number) => {
      if (isLoading) return;

      const current = form.values.affectedRoads;
      if (index < 0 || index >= current.length) return;

      form.setFieldValue(
        'affectedRoads',
        current.filter((_item, itemIndex) => itemIndex !== index)
      );
    },
    [form, isLoading]
  );

  return {
    affectedRoads,
    totalCount: affectedRoads.length,
    addAffectedRoad,
    removeAffectedRoad,
  };
}
